import { HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";

import { Logger } from "@hermes/logger";
import { OriginCheckerService } from "@hermes/utils-generic/services/origin-checker";
import { LOGGER } from "@hermes/web-logger";

import {
    fetchStory,
    fetchHomepageStory,
    fetchStoryFailure,
    fetchStorySuccess,
    fetchHomepageStorySuccess,
} from "../actions/story.action";
import { PageAnalyticsService } from "../service/analytics/page-analytics.service";
import { StoryHeadService } from "../service/head/story-head.service";
import { StoryService } from "../service/story.service";

@Injectable()
export class StoryEffects {
    public fetchStory$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchStory),
            switchMap((action) =>
                this.storyService.fetchStoryFromPath(action.path).pipe(
                    map((response) =>
                        fetchStorySuccess({
                            story: response,
                            searchTerm: action.searchTerm,
                        }),
                    ),
                    catchError((error) => of(fetchStoryFailure({ error }))),
                ),
            ),
        ),
    );

    public fetchStoryHomepage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchHomepageStory),
            switchMap(() =>
                this.storyService.fetchStoryHomePage().pipe(
                    map((response) =>
                        fetchHomepageStorySuccess({
                            story: response,
                        }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(fetchStoryFailure({ error })),
                    ),
                ),
            ),
        ),
    );

    public fetchStoryFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fetchStoryFailure),
                tap((action) => {
                    this.logger.info("Error fetch story editos", action.error);
                    if (action.error.status === 404) {
                        this.router.navigate(["/404-error"], {
                            skipLocationChange: true,
                            replaceUrl: false,
                        });
                    }
                }),
            ),
        { dispatch: false },
    );

    public updateMetaTag$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fetchStorySuccess),
                tap((action) => {
                    this.storyHeadService.addMetaTagsStory(action.story);
                }),
            ),
        { dispatch: false },
    );

    public sendAnalytics$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fetchStorySuccess),
                tap((action) => {
                    this.pageAnalyticsService.sendPageViewEvent(
                        action.story.template,
                        action.story.storyContent.analytics.editorialCategory,
                        action.story.storyContent.analytics.editorialItem,
                        action.story.storyContent.analytics.editorialTypology,
                    );
                    if (
                        action.searchTerm &&
                        this.originCheckerService.checkReferrerOrigin()
                    ) {
                        this.pageAnalyticsService.sendSearchEditoEvent(
                            action.searchTerm,
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    constructor(
        @Inject(LOGGER) private logger: Logger,
        private actions$: Actions,
        private router: Router,
        private originCheckerService: OriginCheckerService,
        private storyService: StoryService,
        private storyHeadService: StoryHeadService,
        private pageAnalyticsService: PageAnalyticsService,
    ) {}
}
